/**
 * Processing Error Codes Constants
 * Maps numeric error codes to their string enum equivalents for API requests
 * Based on the old QMC ProcessingErrorCodesHelper
 */

/**
 * Processing error code string enums used by the API
 */
export enum ProcessingErrorCodeEnum {
  SystemError = "SystemError",
  EmptyScheduledOrder = "EmptyScheduledOrder",
  NoItemsToShip = "NoItemsToShip",
  ShippingRateNotFound = "ShippingRateNotFound",
  PaymentIntegrationNotFound = "PaymentIntegrationNotFound",
  PaymentMethodNull = "PaymentMethodNull",
  PaymentFailed = "PaymentFailed",
  PaymentFailedGatewayCommunication = "PaymentFailedGatewayCommunication",
  ClientOrderCreationFailure = "ClientOrderCreationFailure",
  ClientOrderUpdateFailure = "ClientOrderUpdateFailure",
  ClientOrderCreationInvalidResponse = "ClientOrderCreationInvalidResponse",
}

/**
 * Numeric processing error codes
 */
export enum ProcessingErrorCodeNumber {
  SystemError = 99,
  EmptyScheduledOrder = 1000,
  NoItemsToShip = 1001,
  ShippingRateNotFound = 1002,
  PaymentIntegrationNotFound = 1003,
  PaymentMethodNull = 1004,
  PaymentFailed = 2000,
  PaymentFailedGatewayCommunication = 2001,
  ClientOrderCreationFailure = 3000,
  ClientOrderUpdateFailure = 3001,
  ClientOrderCreationInvalidResponse = 3002,
}

/**
 * Mapping from numeric codes to string enums for API requests
 * This ensures we always send the correct string enum values to the API
 */
export const PROCESSING_ERROR_CODES_MAP: Record<
  number,
  ProcessingErrorCodeEnum
> = {
  [ProcessingErrorCodeNumber.SystemError]: ProcessingErrorCodeEnum.SystemError,
  [ProcessingErrorCodeNumber.EmptyScheduledOrder]:
    ProcessingErrorCodeEnum.EmptyScheduledOrder,
  [ProcessingErrorCodeNumber.NoItemsToShip]:
    ProcessingErrorCodeEnum.NoItemsToShip,
  [ProcessingErrorCodeNumber.ShippingRateNotFound]:
    ProcessingErrorCodeEnum.ShippingRateNotFound,
  [ProcessingErrorCodeNumber.PaymentIntegrationNotFound]:
    ProcessingErrorCodeEnum.PaymentIntegrationNotFound,
  [ProcessingErrorCodeNumber.PaymentMethodNull]:
    ProcessingErrorCodeEnum.PaymentMethodNull,
  [ProcessingErrorCodeNumber.PaymentFailed]:
    ProcessingErrorCodeEnum.PaymentFailed,
  [ProcessingErrorCodeNumber.PaymentFailedGatewayCommunication]:
    ProcessingErrorCodeEnum.PaymentFailedGatewayCommunication,
  [ProcessingErrorCodeNumber.ClientOrderCreationFailure]:
    ProcessingErrorCodeEnum.ClientOrderCreationFailure,
  [ProcessingErrorCodeNumber.ClientOrderUpdateFailure]:
    ProcessingErrorCodeEnum.ClientOrderUpdateFailure,
  [ProcessingErrorCodeNumber.ClientOrderCreationInvalidResponse]:
    ProcessingErrorCodeEnum.ClientOrderCreationInvalidResponse,
};

/**
 * Reverse mapping from string enums to numeric codes
 */
export const PROCESSING_ERROR_ENUM_TO_NUMBER_MAP: Record<
  ProcessingErrorCodeEnum,
  number
> = {
  [ProcessingErrorCodeEnum.SystemError]: ProcessingErrorCodeNumber.SystemError,
  [ProcessingErrorCodeEnum.EmptyScheduledOrder]:
    ProcessingErrorCodeNumber.EmptyScheduledOrder,
  [ProcessingErrorCodeEnum.NoItemsToShip]:
    ProcessingErrorCodeNumber.NoItemsToShip,
  [ProcessingErrorCodeEnum.ShippingRateNotFound]:
    ProcessingErrorCodeNumber.ShippingRateNotFound,
  [ProcessingErrorCodeEnum.PaymentIntegrationNotFound]:
    ProcessingErrorCodeNumber.PaymentIntegrationNotFound,
  [ProcessingErrorCodeEnum.PaymentMethodNull]:
    ProcessingErrorCodeNumber.PaymentMethodNull,
  [ProcessingErrorCodeEnum.PaymentFailed]:
    ProcessingErrorCodeNumber.PaymentFailed,
  [ProcessingErrorCodeEnum.PaymentFailedGatewayCommunication]:
    ProcessingErrorCodeNumber.PaymentFailedGatewayCommunication,
  [ProcessingErrorCodeEnum.ClientOrderCreationFailure]:
    ProcessingErrorCodeNumber.ClientOrderCreationFailure,
  [ProcessingErrorCodeEnum.ClientOrderUpdateFailure]:
    ProcessingErrorCodeNumber.ClientOrderUpdateFailure,
  [ProcessingErrorCodeEnum.ClientOrderCreationInvalidResponse]:
    ProcessingErrorCodeNumber.ClientOrderCreationInvalidResponse,
};

/**
 * User-friendly descriptions for each error code
 */
export const PROCESSING_ERROR_CODE_DESCRIPTIONS: Record<number, string> = {
  [ProcessingErrorCodeNumber.SystemError]: "System Error",
  [ProcessingErrorCodeNumber.EmptyScheduledOrder]: "Empty Scheduled Order",
  [ProcessingErrorCodeNumber.NoItemsToShip]: "No Items To Ship",
  [ProcessingErrorCodeNumber.ShippingRateNotFound]: "Shipping Rate Not Found",
  [ProcessingErrorCodeNumber.PaymentIntegrationNotFound]:
    "Payment Integration Not Found",
  [ProcessingErrorCodeNumber.PaymentMethodNull]: "No Payment Method",
  [ProcessingErrorCodeNumber.PaymentFailed]: "Payment Failed",
  [ProcessingErrorCodeNumber.PaymentFailedGatewayCommunication]:
    "Payment Failed Gateway Communication",
  [ProcessingErrorCodeNumber.ClientOrderCreationFailure]:
    "Client Order Creation Failure",
  [ProcessingErrorCodeNumber.ClientOrderUpdateFailure]:
    "Client Order Update Failure",
  [ProcessingErrorCodeNumber.ClientOrderCreationInvalidResponse]:
    "Client Order Creation Invalid Response",
};

/**
 * Helper functions for processing error code conversions
 */
export class ProcessingErrorCodeHelper {
  /**
   * Convert numeric error codes to string enums for API requests
   */
  static convertCodesToEnums(codes: number[]): ProcessingErrorCodeEnum[] {
    return codes
      .map((code) => PROCESSING_ERROR_CODES_MAP[code])
      .filter((enumValue): enumValue is ProcessingErrorCodeEnum =>
        Boolean(enumValue),
      );
  }

  /**
   * Convert string enums to numeric codes
   */
  static convertEnumsToCodes(enums: ProcessingErrorCodeEnum[]): number[] {
    return enums
      .map((enumValue) => PROCESSING_ERROR_ENUM_TO_NUMBER_MAP[enumValue])
      .filter((code): code is number => typeof code === "number");
  }

  /**
   * Get user-friendly description for a numeric error code
   */
  static getDescription(code: number): string {
    return (
      PROCESSING_ERROR_CODE_DESCRIPTIONS[code] || `Unknown Error (${code})`
    );
  }

  /**
   * Get all available error code options for dropdowns/filters
   */
  static getAllErrorCodeOptions(): Array<{
    code: number;
    description: string;
    enumValue: ProcessingErrorCodeEnum;
  }> {
    return Object.entries(PROCESSING_ERROR_CODE_DESCRIPTIONS).map(
      ([code, description]) => ({
        code: Number(code),
        description,
        enumValue: PROCESSING_ERROR_CODES_MAP[Number(code)],
      }),
    );
  }
}
